import React, { useEffect, useState } from 'react';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { string, object, shape, func, arrayOf, bool } from 'prop-types';
import { eventNames, reportToSegment, types } from '@smartcar/morse';
import { Box, Typography, useTheme } from '@mui/material';

import { gatedFeatureData } from '../../../../services/featureGate';
import FEATURES from '../../../../services/featureGate/features';
import staticText from '../../../../localization/Application/webhooks';
import {
  DeactivateWebhookModal,
  DeleteWebhookModal,
  TestWebhookModal,
  WebhookDetails,
  WebhookErrorModal,
  WebhooksHome,
  WebhooksModal,
  VerifyWebhookModal,
} from './components';
import { SupportModal } from '../TopbarNav/components';
import { Banner, InlineRoute, UpgradeButton } from '../../../../components';
import applicationText from '../../../../localization/Application/application';
import { handleUpgradeClick } from '../../../../components/UpsellModal/UpsellModal';
import { ACTIONS } from './utils/webhookConfig';

const Webhooks = ({
  actions: {
    fetchWebhooksRequest,
    fetchSignalsRequest,
    toggleWebhookModal,
    selectWebhook,
    fetchTestEventRequest,
    clearTestData,
  },
  isFetchingWebhooks,
  signals,
  currentModal,
  testEvent,
  errorFetchingTestEvent,
  webhooks,
  rolePermissions,
  applicationId,
  featureSetId,
  history,
}) => {
  const theme = useTheme();
  const isGated = gatedFeatureData(FEATURES.SCHEDULED_WEBHOOKS, featureSetId);
  const canWriteBilling = rolePermissions.includes('write_billing');
  const hasWebhooks = Object.keys(webhooks).length > 0;
  const {
    VERIFY, ENABLE, EDIT, TEST, DELETE, DISABLE,
  } = ACTIONS;

  const [modalOpen, setModalOpen] = useState(false);

  const toggleSupportModal = () => {
    // Support modal currently reports close event to segment within it, so we just send open event
    if (!modalOpen) {
      reportToSegment(types.TRACK, eventNames.modalOpened,
        { label: applicationText.supportModal.label, title: applicationText.supportModal.title });
    }
    setModalOpen(!modalOpen);
    toggleWebhookModal({ nextModal: null, curModal: 'support', trackCloseEvent: true });
  };

  const openWebhookModal = () => {
    selectWebhook('');
    const modalName = Object.keys(webhooks).length >= 10 ? 'error' : 'add';
    toggleWebhookModal({
      nextModal: modalName,
      curModal: null,
      nextTitle: modalName === 'error' ? staticText.errorConfig.title : '',
    });
  };

  const upgradeBanner = message => (
    <Banner severity="gated" styles={{ marginBottom: 1 }}>
      <Typography variant="body2">{message}</Typography>
    </Banner>
  );

  const messageByPermission = canWriteBilling ?
    staticText.featureGated.description : staticText.featureGated.contactOwnersToUpgrade;

  let upgradeAgainMessage;
  if (isGated && hasWebhooks) {
    const message = canWriteBilling
      ? <InlineRoute text={staticText.featureGated.regainAccessDescription} segmentLabel={`upsell ${FEATURES.SCHEDULED_WEBHOOKS}`} />
      : staticText.featureGated.contactOwnersToRegainAccess;
    upgradeAgainMessage = upgradeBanner(message);
  }

  const upgradeMessage = isGated ? upgradeBanner(messageByPermission) : null;

  const upgradeCta = canWriteBilling ? (
    <UpgradeButton
      text={staticText.featureGated.buttonText}
      feature={FEATURES.SCHEDULED_WEBHOOKS}
      handleClick={() => handleUpgradeClick({
        feature: FEATURES.SCHEDULED_WEBHOOKS,
        history,
      })}
    />
  ) : (
    upgradeBanner(messageByPermission)
  );

  const modalOpener = (modalName) => {
    const modalMap = {
      [VERIFY.key]: <VerifyWebhookModal />,
      [ENABLE.key]: <VerifyWebhookModal />,
      [EDIT.key]: <WebhooksModal mode="edit" />,
      [TEST.key]: <TestWebhookModal />,
      [DELETE.key]: <DeleteWebhookModal />,
      [DISABLE.key]: <DeactivateWebhookModal />,
      error: <WebhookErrorModal {...staticText.errorConfig} />,
      add: <WebhooksModal mode="add" upgradeCta={isGated ? upgradeCta : null} upgradeMessage={upgradeMessage} />,
      support: <SupportModal toggleModal={toggleSupportModal} />,
    };
    return modalMap[modalName.toLowerCase()];
  };

  const clearWebhookData = () => {
    selectWebhook('');
    clearTestData();
    toggleWebhookModal({ nextModal: null, curModal: null });
  };

  useEffect(() => {
    clearWebhookData();
    reportToSegment(types.PAGE, 'Webhooks');
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('add-webhook') === 'true') {
      openWebhookModal();
      urlParams.delete('add-webhook');
      window.history.replaceState({}, '', `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`);
    }
  }, []);

  useEffect(() => {
    fetchWebhooksRequest();
    fetchSignalsRequest();
  }, [isGated]);

  return (
    <Box data-testid="webhooks" sx={{ maxWidth: theme.width.content, minWidth: '900px' }}>
      <Switch>
        <Route
          path={`/apps/${applicationId}/webhooks/:webhookId`}
          render={({ match }) => {
            const { webhookId } = match.params;
            selectWebhook(webhookId);
            if (!webhooks[webhookId] || isGated || webhooks[webhookId].disabled) {
              return <Redirect to={`/apps/${applicationId}/webhooks`} />;
            }
            return (
              <WebhookDetails
                heading={staticText.heading}
                signals={signals.signalsByGroup}
                webhook={webhooks[webhookId]}
                testEvent={testEvent}
                fetchTestEventRequest={fetchTestEventRequest}
                errorFetchingTestEvent={errorFetchingTestEvent}
                toggleWebhookModal={toggleWebhookModal}
              />
              );
            }
          }
        />
        <Route path={`/apps/${applicationId}/webhooks`}>
          <WebhooksHome
            isFetchingWebhooks={isFetchingWebhooks}
            webhooks={webhooks}
            openWebhookModal={openWebhookModal}
            upgradeCta={isGated ? upgradeCta : null}
            upgradeAgainMessage={upgradeAgainMessage}
          />
        </Route>
      </Switch>
      {currentModal && modalOpener(currentModal)}
    </Box>
  );
};

export default withRouter(Webhooks);

Webhooks.propTypes = {
  featureSetId: string.isRequired,
  actions: shape({
    fetchWebhooksRequest: func.isRequired,
    fetchSignalsRequest: func.isRequired,
    toggleWebhookModal: func.isRequired,
    selectWebhook: func.isRequired,
    fetchTestEventRequest: func.isRequired,
    clearTestData: func.isRequired,
  }).isRequired,
  isFetchingWebhooks: bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  webhooks: object,
  signals: shape({
    signalsByGroup: object.isRequired,
  }).isRequired,
  currentModal: string,
  testEvent: object,
  errorFetchingTestEvent: bool.isRequired,
  applicationId: string.isRequired,
  rolePermissions: arrayOf(string).isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

Webhooks.defaultProps = {
  webhooks: {},
  currentModal: null,
  testEvent: null,
};
